import React, { useEffect, useState } from 'react';
import { Button } from '@econsult/econsult-ui/dist/components/Button';
import { Timepicker } from '@econsult/econsult-ui/dist/components/Datepicker';
import { useAppState } from 'store/configureStore';
import { getConsultationResponseTime, saveConsultationResponseTime } from 'store/action/demandManagement';
import { CustomAnchoredToolTip } from 'components/Table';
import { Form, InputRow, FieldSet, LegendInfo, FieldLabelWithPlacement } from 'components/Form';
import { SettingDetail, SettingInfo, SettingsSection, SettingsSectionHeading } from 'components/Setting/style';
import { ParagraphXSmallRegular } from 'components/Typography';
import { ConsultationResponseTimeSetting } from 'components/DemandManagement/style';
import { ConsultationResponseDaysSelect } from './ConsultationResponseDaysSelect';

const initialCutOffTime = '18:30';
const sameDayResponseInDays = 0;

const getFormValues = formElements => {
  const { responseTimeInDays, cutOffTime } = formElements;
  return {
    consultationResponseDays: Number(responseTimeInDays.value),
    patientSubmitConsultationEndTime: cutOffTime?.value,
  };
};

export const ConsultationResponseTime = () => {
  const {
    app: {
      selectedPractice: { id: selectedPracticeId },
      selectedPracticeHasAdminRights,
    },
    featureFlags: {
      SETTINGS_CONSULTATION_RESPONSE_TIME_USER_EDITABLE,
    },
  } = useAppState();

  const [responseTimeInDays, setResponseTimeInDays] = useState(null);
  const [cutOffTime, setCutOffTime] = useState(initialCutOffTime);
  const isUserEditable = SETTINGS_CONSULTATION_RESPONSE_TIME_USER_EDITABLE;

  useEffect(() => {
    setResponseTimeInDays(null);
    setCutOffTime(initialCutOffTime);
    getConsultationResponseTime(selectedPracticeId).then(responseTime => {
      if (!responseTime) {
        return;
      }
      setResponseTimeInDays(responseTime.consultationResponseDays);
      if (responseTime?.patientSubmitConsultationEndTime24HourRepresentation) {
        setCutOffTime(responseTime.patientSubmitConsultationEndTime24HourRepresentation);
      }
    });
  }, [selectedPracticeId]);

  const saveResponseTime = event => {
    event.preventDefault();
    const { consultationResponseDays, patientSubmitConsultationEndTime } = getFormValues(event.target.elements);
    const responseTime = {
      consultationResponseDays: consultationResponseDays,
      patientSubmitConsultationEndTime24HourRepresentation: consultationResponseDays === sameDayResponseInDays
        ? patientSubmitConsultationEndTime
        : null,
    };
    saveConsultationResponseTime(selectedPracticeId, responseTime);
  };

  return (
    <SettingsSection data-automation="ConsultationResponseTimeWrapper">
      <ConsultationResponseTimeSetting>
        <SettingInfo>
          <SettingsSectionHeading as="h3">
            Response time for clinical eConsults
          </SettingsSectionHeading>

          <ParagraphXSmallRegular>
            This allows you to choose the timeframe patients are given to expect a response.
            If you select <strong>Same working day</strong>, you need to set a cut-off time.
            Patients who submit after this time will be told to expect a response by the end of the next working day.
          </ParagraphXSmallRegular>
        </SettingInfo>

        <SettingDetail>
          <Form action="" onSubmit={saveResponseTime} data-automation="CRTForm">
            <FieldSet>
              {!isUserEditable && (
                <LegendInfo data-automation="ResponseTimeSettingInfo">
                  <strong>This setting can only be viewed right now.</strong>
                  <br /><br />
                  <span>To make changes, please contact the support team: practice.support@econsult.health.</span>
                </LegendInfo>
              )}

              <InputRow>
                <FieldLabelWithPlacement column={1} columnSpan={2}>
                  Expected response time
                  <ConsultationResponseDaysSelect
                    responseTimeInDays={responseTimeInDays}
                    onClickResponseDays={setResponseTimeInDays}
                  />
                </FieldLabelWithPlacement>

                {responseTimeInDays === sameDayResponseInDays && (
                  <FieldLabelWithPlacement column={3} columnSpan={3}>
                    Same day cut-off time
                    <Timepicker value={cutOffTime} onChange={event => setCutOffTime(event.target.value)} name="cutOffTime" />
                  </FieldLabelWithPlacement>
                )}

                <Button
                  disabled={!selectedPracticeHasAdminRights || !isUserEditable}
                  type="submit"
                  data-automation="SaveCRTButton"
                  size="small"
                >
                  Save response time
                  {!selectedPracticeHasAdminRights && (
                  <CustomAnchoredToolTip automationLabel="SaveCRTDisabledTooltip" label="Contact your practice admin" align="top" />
                  )}
                </Button>
              </InputRow>
            </FieldSet>
          </Form>
        </SettingDetail>
      </ConsultationResponseTimeSetting>
    </SettingsSection>
  );
};
